import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Stack,
  Rating,
} from '@mui/material';
import { Building2 } from 'lucide-react'; // Example icon
import { mockAgencies, AgencyType } from '../utils/mockData';

const AgenciesListPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
          Find Your Trusted Real Estate Partner
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Explore our network of professional real estate agencies.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {mockAgencies.map((agency: AgencyType) => (
          <Grid item xs={12} sm={6} md={4} key={agency.id}>
            <RouterLink to={`/agencies/${agency.id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="180"
                  image={agency.logoUrl || 'https://via.placeholder.com/300x180?text=Agency+Logo'}
                  alt={`${agency.name} Logo`}
                  sx={{ objectFit: 'contain', p: 1 }} // contain to show full logo, padding for some space
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                    {agency.name}
                  </Typography>
                  
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} color="text.secondary">
                    <Building2 size={18} />
                    <Typography variant="body2">
                      {agency.offices.length} office(s)
                    </Typography>
                  </Stack>

                  <Box sx={{ mb: 1.5 }}>
                    <Rating value={agency.rating} precision={0.1} readOnly size="small" />
                  </Box>

                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{
                      display: '-webkit-box',
                      '-webkit-line-clamp': 3,
                      '-webkit-box-orient': 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minHeight: '3.8em' // Approx 3 lines
                    }}>
                      {agency.description}
                    </Typography>
                  </Box>
                  
                  <Box>
                    {agency.specialties.slice(0, 3).map(spec => (
                      <Chip 
                        key={spec} 
                        label={spec} 
                        size="small" 
                        sx={{ 
                          mr: 0.5, 
                          mb: 0.5,
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          }
                        }} 
                      />
                    ))}
                    {agency.specialties.length > 3 && (
                      <Chip label="..." size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </RouterLink>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default AgenciesListPage;
